<h2 mat-dialog-title class="dialog-header">
    <mat-icon class="header-icon">cleaning_services</mat-icon>
    Hat Cleanup

    <span class="header-spacer"></span>

    <button mat-icon-button mat-dialog-close class="close-button" matTooltip="Close">
        <mat-icon>close</mat-icon>
    </button>
</h2>

@if (processing) {
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
}

<mat-dialog-content>
    <mat-tab-group [(selectedIndex)]="activeTabIndex" (selectedIndexChange)="onTabChanged($event)" class="cleanup-tabs">

        <!-- ═══ Active Hats Tab ═══ -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon entity-hats">school</mat-icon>
                Active Hats
                <span class="tab-badge">{{ allHatsData.length }}</span>
            </ng-template>

            <div class="cleanup-container">

                <!-- ── Search & Filters ── -->
                <shared-org-search-bar
                    searchLabel="Search hats"
                    searchPlaceholder="Search by name, staff member, group..."
                    (searchChanged)="onSearchChanged($event)">
                </shared-org-search-bar>

                <shared-org-filter-bar
                    [filters]="filterConfig"
                    [rightFilters]="rightFilterConfig"
                    [totalCount]="allHatsData.length"
                    activeColor="#e53935"
                    activeDarkColor="#ef5350"
                    (filtersChanged)="onFiltersChanged($event)">
                </shared-org-filter-bar>

                <!-- ── Action Toolbar ── -->
                @if (selection.selected.length > 0) {
                    <div class="action-toolbar">
                        <span class="selection-count">
                            <mat-icon>check_circle</mat-icon>
                            {{ selection.selected.length }} hat(s) selected
                        </span>
                        <button mat-raised-button color="warn" (click)="bulkDelete()" [disabled]="processing"
                                matTooltip="Soft-delete selected hats that have no users, no posts, and no special rights">
                            <mat-icon>delete_sweep</mat-icon>
                            Delete Selected
                        </button>
                        <button mat-raised-button color="accent" (click)="bulkRemoveUsers()" [disabled]="processing"
                                matTooltip="Remove users from selected hats that have no posts and no special rights">
                            <mat-icon>person_remove</mat-icon>
                            Remove Users
                        </button>
                        <button mat-stroked-button (click)="selection.clear()" [disabled]="processing">
                            Clear Selection
                        </button>
                    </div>
                }

                <!-- ── Table ── -->
                <div class="table-scroll-container">
                    @if (dataSource.data.length === 0) {
                        <div class="empty-state">
                            <mat-icon>sentiment_satisfied_alt</mat-icon>
                            <p>No hats match the current filters</p>
                            <span>Try adjusting your search or filters</span>
                        </div>
                    } @else {
                        <mat-table [dataSource]="dataSource" class="cleanup-table">

                            <!-- Checkbox Column -->
                            <ng-container matColumnDef="select">
                                <mat-header-cell *matHeaderCellDef class="select-col">
                                    <mat-checkbox
                                        [checked]="isAllSelected()"
                                        [indeterminate]="selection.hasValue() && !isAllSelected()"
                                        (change)="toggleAllRows()">
                                    </mat-checkbox>
                                </mat-header-cell>
                                <mat-cell *matCellDef="let row" class="select-col">
                                    <mat-checkbox
                                        [checked]="selection.isSelected(row)"
                                        (change)="selection.toggle(row)">
                                    </mat-checkbox>
                                </mat-cell>
                            </ng-container>

                            <!-- Name Column -->
                            <ng-container matColumnDef="name">
                                <mat-header-cell *matHeaderCellDef class="sortable-header" (click)="toggleSort('name')">
                                    Name
                                    @if (sortColumn === 'name') {
                                        <mat-icon class="sort-arrow">{{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
                                    }
                                </mat-header-cell>
                                <mat-cell *matCellDef="let row">
                                    <div class="name-cell">
                                        <span class="hat-name">{{ row.name || '(unnamed)' }}</span>
                                        @if (isDeletable(row)) {
                                            <span class="status-chip deletable" matTooltip="No users, no posts, no special rights — can be deleted">
                                                <mat-icon>delete_sweep</mat-icon> Deletable
                                            </span>
                                        } @else if (hasRemovableUsers(row)) {
                                            <span class="status-chip removable" matTooltip="Has users but no posts and no special rights — users can be removed">
                                                <mat-icon>person_remove</mat-icon> Removable Users
                                            </span>
                                        }
                                    </div>
                                </mat-cell>
                            </ng-container>

                            <!-- Users Column -->
                            <ng-container matColumnDef="users">
                                <mat-header-cell *matHeaderCellDef class="users-col sortable-header" (click)="toggleSort('users')">
                                    Staff
                                    @if (sortColumn === 'users') {
                                        <mat-icon class="sort-arrow">{{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
                                    }
                                </mat-header-cell>
                                <mat-cell *matCellDef="let row" class="users-col">
                                    @if (getUsersForHat(row).length === 0 && getPendingUsersForHat(row).length === 0) {
                                        <span class="no-data">None</span>
                                    } @else {
                                        <div class="stacked-avatars" [matTooltip]="getUserNamesTooltip(row)">
                                            @for (user of getUsersForHat(row) | slice:0:3; track user.key) {
                                                @if ((user.photoThumbnail || user.photoURL) && !brokenAvatars[user.key]) {
                                                    <img class="avatar" [src]="user.photoThumbnail || user.photoURL" [alt]="user.name"
                                                         (error)="brokenAvatars[user.key] = true" />
                                                } @else {
                                                    <span class="avatar avatar-initials" [style.background]="user.profileColor || '#9e9e9e'" style="color: #fff">{{ getInitials(user.name) }}</span>
                                                }
                                            }
                                            @if (getUsersForHat(row).length > 3) {
                                                <span class="avatar avatar-overflow">+{{ getUsersForHat(row).length - 3 }}</span>
                                            }
                                            @for (user of getPendingUsersForHat(row) | slice:0:3; track user.key) {
                                                <span class="pending-avatar-wrap">
                                                    @if ((user.photoThumbnail || user.photoURL) && !brokenAvatars[user.key]) {
                                                        <img class="avatar avatar-pending" [src]="user.photoThumbnail || user.photoURL" [alt]="user.name"
                                                             (error)="brokenAvatars[user.key] = true" />
                                                    } @else {
                                                        <span class="avatar avatar-initials avatar-pending" [style.background]="user.profileColor || '#9e9e9e'" style="color: #fff">{{ getInitials(user.name) }}</span>
                                                    }
                                                </span>
                                            }
                                            @if (getPendingUsersForHat(row).length > 3) {
                                                <span class="pending-avatar-wrap">
                                                    <span class="avatar avatar-overflow avatar-pending">+{{ getPendingUsersForHat(row).length - 3 }}</span>
                                                </span>
                                            }
                                        </div>
                                    }
                                </mat-cell>
                            </ng-container>

                            <!-- Security Groups Column -->
                            <ng-container matColumnDef="securityGroups">
                                <mat-header-cell *matHeaderCellDef class="sg-col sortable-header" (click)="toggleSort('securityGroups')">
                                    Security Groups
                                    @if (sortColumn === 'securityGroups') {
                                        <mat-icon class="sort-arrow">{{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
                                    }
                                </mat-header-cell>
                                <mat-cell *matCellDef="let row" class="sg-col">
                                    @if (getSecurityGroupNames(row).length === 0) {
                                        <span class="no-data">None</span>
                                    } @else {
                                        <span class="sg-count" [matTooltip]="getSecurityGroupNames(row).join(', ')">
                                            <mat-icon class="sg-icon">lock</mat-icon>
                                            {{ getSecurityGroupNames(row).length }}
                                        </span>
                                    }
                                </mat-cell>
                            </ng-container>

                            <!-- Positions Column -->
                            <ng-container matColumnDef="positions">
                                <mat-header-cell *matHeaderCellDef class="scope-col sortable-header" (click)="toggleSort('positions')">
                                    Posts
                                    @if (sortColumn === 'positions') {
                                        <mat-icon class="sort-arrow">{{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
                                    }
                                </mat-header-cell>
                                <mat-cell *matCellDef="let row" class="scope-col">
                                    @if (row.isSpecialRights) {
                                        <span class="scope-chip scope-orgwide" matTooltip="Special Rights hat — applies org-wide">
                                            <mat-icon class="scope-icon">public</mat-icon>
                                            Special Rights
                                        </span>
                                    } @else if (isPositionless(row)) {
                                        <span class="scope-chip scope-no-position" matTooltip="Not linked to any org post">
                                            <mat-icon class="scope-icon">block</mat-icon>
                                            No Post
                                        </span>
                                    } @else {
                                        <span class="scope-chip scope-positioned"
                                              [matTooltip]="getPositionTooltip(row)">
                                            <mat-icon class="scope-icon">account_tree</mat-icon>
                                            @if (getPositionNames(row).length === 1) {
                                                {{ getPositionNames(row)[0] }}
                                            } @else {
                                                {{ getPositionNames(row)[0] }} (+{{ getPositionNames(row).length - 1 }})
                                            }
                                        </span>
                                    }
                                </mat-cell>
                            </ng-container>

                            <!-- Actions Column -->
                            <ng-container matColumnDef="actions">
                                <mat-header-cell *matHeaderCellDef class="actions-col"></mat-header-cell>
                                <mat-cell *matCellDef="let row" class="actions-col">
                                    <button mat-icon-button color="warn" matTooltip="Delete this hat" (click)="singleDelete(row)" [disabled]="processing">
                                        <mat-icon>delete</mat-icon>
                                    </button>
                                    <button mat-icon-button color="accent" matTooltip="Remove users from this hat" (click)="singleRemoveUsers(row)" [disabled]="processing">
                                        <mat-icon>person_remove</mat-icon>
                                    </button>
                                </mat-cell>
                            </ng-container>

                            <mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></mat-header-row>
                            <mat-row *matRowDef="let row; columns: displayedColumns;"
                                [class.deletable-row]="isDeletable(row)"
                                [class.removable-row]="hasRemovableUsers(row)"
                                [class.selected-row]="selection.isSelected(row)">
                            </mat-row>
                        </mat-table>
                    }
                </div>

            </div>
        </mat-tab>

        <!-- ═══ Deleted Hats Tab ═══ -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon tab-icon-deleted">delete_outline</mat-icon>
                Deleted Hats
                <span class="tab-badge tab-badge-deleted">{{ allDeletedHats.length }}</span>
            </ng-template>

            <div class="cleanup-container">

                <shared-org-search-bar
                    searchLabel="Search deleted hats"
                    searchPlaceholder="Search by name, staff member, group..."
                    (searchChanged)="onDeletedSearchChanged($event)">
                </shared-org-search-bar>

                <shared-org-filter-bar
                    [filters]="deletedFilterConfig"
                    [rightFilters]="deletedRightFilterConfig"
                    [totalCount]="allDeletedHats.length"
                    activeColor="#757575"
                    activeDarkColor="#9e9e9e"
                    (filtersChanged)="onDeletedFiltersChanged($event)">
                </shared-org-filter-bar>

                <!-- ── Restore Toolbar ── -->
                @if (deletedSelection.selected.length > 0) {
                    <div class="action-toolbar restore-toolbar">
                        <span class="selection-count">
                            <mat-icon>check_circle</mat-icon>
                            {{ deletedSelection.selected.length }} hat(s) selected
                        </span>
                        <button mat-raised-button color="primary" (click)="bulkRestore()" [disabled]="processing"
                                matTooltip="Restore selected hats — they will become active again">
                            <mat-icon>restore</mat-icon>
                            Restore Selected
                        </button>
                        <button mat-stroked-button (click)="deletedSelection.clear()" [disabled]="processing">
                            Clear Selection
                        </button>
                    </div>
                }

                <!-- ── Deleted Table ── -->
                <div class="table-scroll-container">
                    @if (deletedDataSource.data.length === 0) {
                        <div class="empty-state">
                            <mat-icon>delete_outline</mat-icon>
                            <p>No deleted hats found</p>
                            <span>Deleted hats will appear here and can be restored</span>
                        </div>
                    } @else {
                        <mat-table [dataSource]="deletedDataSource" class="cleanup-table deleted-table">

                            <!-- Checkbox Column -->
                            <ng-container matColumnDef="select">
                                <mat-header-cell *matHeaderCellDef class="select-col">
                                    <mat-checkbox
                                        [checked]="isAllDeletedSelected()"
                                        [indeterminate]="deletedSelection.hasValue() && !isAllDeletedSelected()"
                                        (change)="toggleAllDeletedRows()">
                                    </mat-checkbox>
                                </mat-header-cell>
                                <mat-cell *matCellDef="let row" class="select-col">
                                    <mat-checkbox
                                        [checked]="deletedSelection.isSelected(row)"
                                        (change)="deletedSelection.toggle(row)">
                                    </mat-checkbox>
                                </mat-cell>
                            </ng-container>

                            <!-- Name Column -->
                            <ng-container matColumnDef="name">
                                <mat-header-cell *matHeaderCellDef class="sortable-header" (click)="toggleDeletedSort('name')">
                                    Name
                                    @if (deletedSortColumn === 'name') {
                                        <mat-icon class="sort-arrow">{{ deletedSortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
                                    }
                                </mat-header-cell>
                                <mat-cell *matCellDef="let row">
                                    <div class="name-cell">
                                        <span class="hat-name deleted-name">{{ row.name || '(unnamed)' }}</span>
                                    </div>
                                </mat-cell>
                            </ng-container>

                            <!-- Users Column -->
                            <ng-container matColumnDef="users">
                                <mat-header-cell *matHeaderCellDef class="users-col sortable-header" (click)="toggleDeletedSort('users')">
                                    Staff
                                    @if (deletedSortColumn === 'users') {
                                        <mat-icon class="sort-arrow">{{ deletedSortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
                                    }
                                </mat-header-cell>
                                <mat-cell *matCellDef="let row" class="users-col">
                                    @if (getUsersForHat(row).length === 0 && getPendingUsersForHat(row).length === 0) {
                                        <span class="no-data">None</span>
                                    } @else {
                                        <div class="stacked-avatars" [matTooltip]="getUserNamesTooltip(row)">
                                            @for (user of getUsersForHat(row) | slice:0:3; track user.key) {
                                                @if ((user.photoThumbnail || user.photoURL) && !brokenAvatars[user.key]) {
                                                    <img class="avatar" [src]="user.photoThumbnail || user.photoURL" [alt]="user.name"
                                                         (error)="brokenAvatars[user.key] = true" />
                                                } @else {
                                                    <span class="avatar avatar-initials" [style.background]="user.profileColor || '#9e9e9e'" style="color: #fff">{{ getInitials(user.name) }}</span>
                                                }
                                            }
                                            @if (getUsersForHat(row).length > 3) {
                                                <span class="avatar avatar-overflow">+{{ getUsersForHat(row).length - 3 }}</span>
                                            }
                                            @for (user of getPendingUsersForHat(row) | slice:0:3; track user.key) {
                                                <span class="pending-avatar-wrap">
                                                    @if ((user.photoThumbnail || user.photoURL) && !brokenAvatars[user.key]) {
                                                        <img class="avatar avatar-pending" [src]="user.photoThumbnail || user.photoURL" [alt]="user.name"
                                                             (error)="brokenAvatars[user.key] = true" />
                                                    } @else {
                                                        <span class="avatar avatar-initials avatar-pending" [style.background]="user.profileColor || '#9e9e9e'" style="color: #fff">{{ getInitials(user.name) }}</span>
                                                    }
                                                </span>
                                            }
                                            @if (getPendingUsersForHat(row).length > 3) {
                                                <span class="pending-avatar-wrap">
                                                    <span class="avatar avatar-overflow avatar-pending">+{{ getPendingUsersForHat(row).length - 3 }}</span>
                                                </span>
                                            }
                                        </div>
                                    }
                                </mat-cell>
                            </ng-container>

                            <!-- Security Groups Column -->
                            <ng-container matColumnDef="securityGroups">
                                <mat-header-cell *matHeaderCellDef class="sg-col sortable-header" (click)="toggleDeletedSort('securityGroups')">
                                    Security Groups
                                    @if (deletedSortColumn === 'securityGroups') {
                                        <mat-icon class="sort-arrow">{{ deletedSortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
                                    }
                                </mat-header-cell>
                                <mat-cell *matCellDef="let row" class="sg-col">
                                    @if (getSecurityGroupNames(row).length === 0) {
                                        <span class="no-data">None</span>
                                    } @else {
                                        <span class="sg-count" [matTooltip]="getSecurityGroupNames(row).join(', ')">
                                            <mat-icon class="sg-icon">lock</mat-icon>
                                            {{ getSecurityGroupNames(row).length }}
                                        </span>
                                    }
                                </mat-cell>
                            </ng-container>

                            <!-- Positions Column -->
                            <ng-container matColumnDef="positions">
                                <mat-header-cell *matHeaderCellDef class="scope-col sortable-header" (click)="toggleDeletedSort('positions')">
                                    Posts
                                    @if (deletedSortColumn === 'positions') {
                                        <mat-icon class="sort-arrow">{{ deletedSortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
                                    }
                                </mat-header-cell>
                                <mat-cell *matCellDef="let row" class="scope-col">
                                    @if (row.isSpecialRights) {
                                        <span class="scope-chip scope-orgwide" matTooltip="Special Rights hat — applies org-wide">
                                            <mat-icon class="scope-icon">public</mat-icon>
                                            Special Rights
                                        </span>
                                    } @else if (isPositionless(row)) {
                                        <span class="scope-chip scope-no-position" matTooltip="Not linked to any org post">
                                            <mat-icon class="scope-icon">block</mat-icon>
                                            No Post
                                        </span>
                                    } @else {
                                        <span class="scope-chip scope-positioned"
                                              [matTooltip]="getPositionTooltip(row)">
                                            <mat-icon class="scope-icon">account_tree</mat-icon>
                                            @if (getPositionNames(row).length === 1) {
                                                {{ getPositionNames(row)[0] }}
                                            } @else {
                                                {{ getPositionNames(row)[0] }} (+{{ getPositionNames(row).length - 1 }})
                                            }
                                        </span>
                                    }
                                </mat-cell>
                            </ng-container>

                            <!-- Actions Column -->
                            <ng-container matColumnDef="actions">
                                <mat-header-cell *matHeaderCellDef class="actions-col"></mat-header-cell>
                                <mat-cell *matCellDef="let row" class="actions-col">
                                    <button mat-icon-button color="primary" matTooltip="Restore this hat" (click)="singleRestore(row)" [disabled]="processing">
                                        <mat-icon>restore</mat-icon>
                                    </button>
                                </mat-cell>
                            </ng-container>

                            <mat-header-row *matHeaderRowDef="deletedDisplayedColumns; sticky: true"></mat-header-row>
                            <mat-row *matRowDef="let row; columns: deletedDisplayedColumns;"
                                [class.selected-row]="deletedSelection.isSelected(row)">
                            </mat-row>
                        </mat-table>
                    }
                </div>

            </div>
        </mat-tab>

    </mat-tab-group>

    <!-- ── Action Log (shared across tabs) ── -->
    @if (actionLog.length > 0 && showActionLog) {
        <div class="action-log">
            <div class="log-header">
                <mat-icon>receipt_long</mat-icon>
                Action Log ({{ actionLog.length }})
                <span class="log-spacer"></span>
                <button mat-icon-button (click)="showActionLog = false" matTooltip="Dismiss log" class="log-close">
                    <mat-icon>close</mat-icon>
                </button>
            </div>
            @for (entry of actionLog; track entry) {
                <div class="log-entry">{{ entry }}</div>
            }
        </div>
    }

</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-button mat-dialog-close>Close</button>
</mat-dialog-actions>
